import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Users, ShieldCheck, FileText, Handshake, Calendar, Search, ClipboardCheck, UserCheck,
} from "lucide-react";
import Seo from "@/components/Seo";
import SectionHeading from "@/components/SectionHeading";
import ServiceCard from "@/components/ServiceCard";
import ArticleCard from "@/components/ArticleCard";
import TestimonialCard from "@/components/TestimonialCard";
import CTABand from "@/components/CTABand";
import api from "@/lib/api";

const VALUES = [
  { icon: Users, title: "Personalised Advice", text: "Tailored to your situation." },
  { icon: ShieldCheck, title: "Transparent Fees", text: "No hidden costs." },
  { icon: FileText, title: "Up-to-date Guidance", text: "Based on current UK rules." },
  { icon: Handshake, title: "Support at Every Stage", text: "From first advice to settlement." },
];

const STEPS = [
  { icon: Calendar, title: "Book & Consultation", text: "Speak with us about your situation and goals." },
  { icon: Search, title: "We Assess Your Options", text: "We review your circumstances and provide clear advice." },
  { icon: ClipboardCheck, title: "Preparation & Support", text: "We help you prepare your application and evidence." },
  { icon: UserCheck, title: "Application & Next Steps", text: "We submit or guide you through the process." },
];

export default function Home() {
  const [individuals, setIndividuals] = useState([]);
  const [articles, setArticles] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/public/individuals").then((r) => setIndividuals(r.data.slice(0, 6))).catch(() => {});
    api.get("/public/articles").then((r) => setArticles(r.data.slice(0, 3))).catch(() => {});
    api.get("/public/testimonials").then((r) => setTestimonials(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <>
      <Seo title="ApexPath | Trusted UK Immigration Advice"
        description="Clear, practical and trusted UK immigration advice for individuals, families, students and businesses." />

      {/* HERO */}
      <section className="relative overflow-hidden bg-navy">
        <img src="/images/home-hero.jpg" alt="Families and professionals in the UK" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(11,25,44,0.94) 0%, rgba(11,25,44,0.78) 45%, rgba(11,25,44,0.35) 100%)" }} />
        <div className="container-x relative grid min-h-[560px] items-center py-16">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="max-w-2xl">
            <span className="eyebrow">UK Immigration Advice for People, Families & Businesses</span>
            <h1 className="mt-4 font-serif text-4xl font-bold leading-[1.1] text-white sm:text-5xl lg:text-6xl">
              From Advice to Settlement, <span className="text-gold">We Walk the Path with You.</span>
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-200 sm:text-lg">
              Clear, practical and trusted UK immigration advice to help individuals, families,
              students and businesses build their future in the UK.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link to="/book-consultation" data-testid="hero-cta-book-consultation" className="btn-gold">
                Book a Consultation <ArrowRight size={18} />
              </Link>
              <Link to="/services" data-testid="hero-cta-explore-services" className="btn-outline-light">
                Explore Our Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* VALUE BAR */}
      <section className="border-b border-slate-200 bg-white">
        <div className="container-x grid grid-cols-2 gap-6 py-8 lg:grid-cols-4">
          {VALUES.map((v) => (
            <div key={v.title} className="flex items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gold/10 text-gold">
                <v.icon size={20} />
              </div>
              <div>
                <div className="text-sm font-semibold text-navy">{v.title}</div>
                <div className="text-xs text-slate-500">{v.text}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-cream py-20">
        <div className="container-x">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <SectionHeading eyebrow="Our Immigration Services"
              title="Practical Support for Your Immigration Journey"
              subtitle="We provide advice and support across a wide range of UK immigration matters for individuals, families and businesses." />
            <Link to="/services" className="link-underline mb-10 hidden items-center gap-1.5 text-sm font-semibold text-navy sm:inline-flex">
              View all services <ArrowRight size={15} />
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {individuals.map((s) => <ServiceCard key={s.id} service={s} />)}
          </div>
        </div>
      </section>

      {/* TWO BANDS */}
      <section className="grid grid-cols-1 md:grid-cols-2">
        <div className="relative min-h-[380px] overflow-hidden p-10 lg:p-14">
          <img src="/images/ind-band.jpg" alt="Family supported by ApexPath" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(11,25,44,0.94) 0%, rgba(11,25,44,0.80) 45%, rgba(11,25,44,0.42) 100%)" }} />
          <div className="relative">
            <span className="eyebrow">For Individuals & Families</span>
            <h3 className="mt-3 font-serif text-3xl font-semibold text-white lg:text-4xl">Building Brighter Futures Together.</h3>
            <p className="mt-4 max-w-md text-base leading-relaxed text-slate-100">
              We help individuals and families navigate the immigration system with clarity, care and practical support at every step.
            </p>
            <Link to="/individuals" className="btn-gold mt-6 text-sm">Explore Individual Services <ArrowRight size={15} /></Link>
          </div>
        </div>
        <div className="relative min-h-[380px] overflow-hidden p-10 lg:p-14">
          <img src="/images/biz-band.jpg" alt="Business professionals supported by ApexPath" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(7,17,30,0.94) 0%, rgba(7,17,30,0.80) 45%, rgba(7,17,30,0.42) 100%)" }} />
          <div className="relative">
            <span className="eyebrow">For Businesses</span>
            <h3 className="mt-3 font-serif text-3xl font-semibold text-white lg:text-4xl">Supporting Global Talent and Business Growth.</h3>
            <p className="mt-4 max-w-md text-base leading-relaxed text-slate-100">
              We help UK businesses recruit and retain global talent, from skilled worker routes to business visitor guidance.
            </p>
            <Link to="/business" className="btn-gold mt-6 text-sm">Explore Business Services <ArrowRight size={15} /></Link>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-white py-20">
        <div className="container-x">
          <SectionHeading eyebrow="Our Process" title="A Clear and Structured Journey"
            subtitle="From your first consultation to a successful outcome, we guide you every step of the way." />
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <div key={s.title} className="relative">
                <div className="mb-4 flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-gold font-serif text-lg font-bold text-navy">{i + 1}</span>
                  <s.icon size={22} className="text-gold" />
                </div>
                <h4 className="text-base font-semibold text-navy">{s.title}</h4>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="bg-cream py-20">
          <div className="container-x">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <SectionHeading eyebrow="What Our Clients Say" title="Real People. Real Journeys." />
              <Link to="/testimonials" className="link-underline mb-10 hidden items-center gap-1.5 text-sm font-semibold text-navy sm:inline-flex">
                View more testimonials <ArrowRight size={15} />
              </Link>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {testimonials.map((t) => <TestimonialCard key={t.id} testimonial={t} />)}
            </div>
          </div>
        </section>
      )}

      {/* INSIGHTS */}
      {articles.length > 0 && (
        <section className="bg-white py-20">
          <div className="container-x">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <SectionHeading eyebrow="Insights & Resources" title="Latest From ApexPath" />
              <Link to="/insights" className="link-underline mb-10 hidden items-center gap-1.5 text-sm font-semibold text-navy sm:inline-flex">
                View all insights <ArrowRight size={15} />
              </Link>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {articles.map((a) => <ArticleCard key={a.id} article={a} />)}
            </div>
          </div>
        </section>
      )}

      <CTABand title="Take the Next Step"
        text="Get clear, practical advice from our immigration experts. We will listen to your situation, explain your options and help you choose the best route forward." />
    </>
  );
}
